import {Component, inject, Input, OnInit} from '@angular/core';
import {AgGridAngular} from "ag-grid-angular";
import {AsyncPipe, DatePipe} from "@angular/common";
import {ContainerComponent} from "@coreui/angular";
import {HasPermissionDirective} from "../../../directives/permissions/has-permission";
import {MatButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {FaqScopeTypePipe} from "../../../pipes/faq-scope-type.pipe";
import {FrequentlyAskedQuestionsService} from "../../../services/frequently-asked-questions.service";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {MatDialog} from "@angular/material/dialog";
import {PermissionTypes} from "../../../enum/PermissionTypes";
import {ColDef, GridApi, GridReadyEvent, SizeColumnsToFitGridStrategy} from "ag-grid-community";
import {IFrequentlyAskedQuestions} from "../../../interfaces/IFrequentlyAskedQuestions";
import {map, Observable} from "rxjs";
import {FormatBoolean} from "../../../components/cell-renderers/show-played/format-boolean.component";
import {
  FaqTableSettingComponents
} from "../../../components/frequently-asked-questions/faq-table-settings/faq-table-setting-components.component";
import {FaqAddComponent} from "../../../components/frequently-asked-questions/faq-add/faq-add.component";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {MatProgressSpinner} from "@angular/material/progress-spinner";

@Component({
  selector: 'app-edit-frequently-asked-questions',
  standalone: true,
  imports: [
    AgGridAngular,
    AsyncPipe,
    ContainerComponent,
    MatButton,
    MatIcon,
    MatTooltip,
    MatProgressSpinner
  ],
  templateUrl: './edit-frequently-asked-questions.component.html',
  styleUrl: './edit-frequently-asked-questions.component.css'
})
export class EditFrequentlyAskedQuestionsComponent implements OnInit{
  @Input() canEditWebappFaqs!: boolean;
  @Input() canEditAdminAppFaqs!: boolean;

  private datePipe: DatePipe = inject(DatePipe);
  private faqScopeTypePipe: FaqScopeTypePipe = inject(FaqScopeTypePipe);
  private frequentlyAskedQuestionsService: FrequentlyAskedQuestionsService = inject(FrequentlyAskedQuestionsService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private matDialog: MatDialog = inject(MatDialog);

  protected readonly permissionTypes = PermissionTypes;
  public autoSizeStrategy: SizeColumnsToFitGridStrategy = {
    type: 'fitGridWidth'
  };
  public defaultColDef: ColDef = {
    flex: 1,
    cellDataType: false,
  };

  public uiState: UIStateEnum = UIStateEnum.ShowLoading;
  public UIStateEnum = UIStateEnum;

  public faqsGridApi!: GridApi<IFrequentlyAskedQuestions>;
  public selectFaqs$!: Observable<IFrequentlyAskedQuestions[]>;
  public faqDefs: ColDef[] = [
    {
      headerName: 'Id',
      flex: 1,
      field: 'Id',
    },
    {
      headerName: 'Scope',
      field: 'ScopeType',
      valueFormatter: params => {
        return this.faqScopeTypePipe.transform(params.value);
      }
    },
    {
      headerName: 'Question',
      field: 'Question',
    },
    {
      headerName: 'Answer',
      field: 'Answer'
    },
    {
      headerName: 'Created On Date',
      maxWidth: 140,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y');
        return convertedDate ? convertedDate : '';
      },
      field: 'CreatedOn'
    },
    {
      headerName: 'Active',
      maxWidth: 80,
      cellRenderer: FormatBoolean,
      field: 'Active',
    },
    {
      maxWidth: 80,
      cellRenderer: FaqTableSettingComponents
    }
  ];

  ngOnInit(): void {
    // Filter FAQs to only show the ones the user can edit
    this.selectFaqs$ = this.frequentlyAskedQuestionsService.selectFaqs$().pipe(
      map(faqs => faqs.filter(faq => {
        if (faq.ScopeType === 1 && this.canEditWebappFaqs) return true;
        if (faq.ScopeType === 2 && this.canEditAdminAppFaqs) return true;
        return false;
      }))
    );

    this.frequentlyAskedQuestionsService.fetchAllFaqs().subscribe({
      next: () => {
        this.uiState = UIStateEnum.ShowData;
      }, error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
        this.uiState = UIStateEnum.ShowData;
      }
    })
  }

  onFAQGridReady(params: GridReadyEvent<IFrequentlyAskedQuestions>) {
    this.faqsGridApi = params.api;
    this.faqsGridApi.sizeColumnsToFit({
      defaultMinWidth: 90,
    });
  }

  onAddFaqClick() {
    this.matDialog.open(FaqAddComponent, {
      width: '650px',
      data: { canEditWebappFaqs: this.canEditWebappFaqs, canEditAdminAppFaqs: this.canEditAdminAppFaqs }
    });
  }

}
